import { useBooleanState } from "../../../common/hooks/useBooleanState";
import { type MutableRefObject } from "react";
import { IconType } from "../../../common/components/icon/IconType";
import {
  FULLWIDTH_MENU_BACKDROP_PROPS,
  SimpleMenu,
  useFullwidthPopoverStyles,
} from "../../../common/components/Menu/Menu";
import { MENU_POS_BELOW } from "../../../common/components/Menu/MenuPositions";
import { useDispatch, useSelector } from "react-redux";
import { MobileNavigationList, NavigationOverview } from "./NavigationList";
import { type NavigationNode } from "../../../Header/types";
import {
  isNavigationLoading,
  selectMobileVisibleParentNodes,
  selectNavigationDisplayMode,
} from "../../../Header/redux/navigationSelectors";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { closeNavigationAction } from "../../../Header/redux/actions";
import { MobileNavButton } from "./MobileNavButton";
import { GoBackLink } from "./GoBackLink";
import { Col, Row } from "../../../common/components/Layout";
import { NavigationSeriesView } from "./NavigationSeriesView";
import { SearchDisplayMode } from "~/Search/models";

interface MobileNavigationProps {
  menuAnchorRef?: MutableRefObject<HTMLElement | null>;
}

/**
 * Contains:
 * * Hamburger button to open category navigation
 * * Category navigation
 */
export const TabletCategoryNavigation = ({ menuAnchorRef }: MobileNavigationProps) => {
  const { val: isOpen, setFalse: close, toggle } = useBooleanState(false);

  const isLoading = useSelector(isNavigationLoading);
  const visibleNodes = useSelector(selectMobileVisibleParentNodes);

  const dispatch = useDispatch();
  const popoverClasses = useFullwidthPopoverStyles();

  const highestSelectedColumn = getHighestSelectedLevel(visibleNodes);

  const transformVal = highestSelectedColumn > 2 ? `translateX(-${(highestSelectedColumn - 2) * 50}%)` : undefined;
  const getShowBack = (highestVisibleColumn: number) => highestVisibleColumn > 2;

  const displayMode = useSelector(selectNavigationDisplayMode);

  return (
    <>
      {isLoading && <CircleSpinner className={"my-auto"} />}
      {!isLoading && <MobileNavButton onClick={toggle} icon={isOpen ? IconType.Close : IconType.List} />}
      <SimpleMenu
        open={isOpen}
        onClose={close}
        TransitionProps={{
          onExited: () => dispatch(closeNavigationAction()),
        }}
        anchorEl={menuAnchorRef?.current ?? null}
        PopoverClasses={popoverClasses}
        marginThreshold={0}
        BackdropProps={FULLWIDTH_MENU_BACKDROP_PROPS}
        {...MENU_POS_BELOW}
      >
        <div className={"scha__tablet-category-navigation__container"}>
          {displayMode === SearchDisplayMode.LIST && (
            <>
              {getShowBack(highestSelectedColumn) && (
                <GoBackLink className={"scha__mobile-category-navigation__go-back"} visibleColAmount={2} />
              )}
              <div
                className={"scha__mobile-category-navigation__container"}
                style={{ transition: "transform 600ms", transform: transformVal }}
              >
                {visibleNodes.map(({ curr, prev }, index, list) => (
                  <div className={"scha__mobile-category-navigation__column"} key={index}>
                    {[
                      curr && <NavigationColumn key={curr.id} parentNode={curr} active={true} />,
                      prev && prev !== curr && <NavigationColumn key={prev.id} parentNode={prev} active={false} />,
                    ]}
                  </div>
                ))}
              </div>
            </>
          )}
          {displayMode === SearchDisplayMode.SERIES_TREE && (
            <>
              <Row>
                <Col size={6}>
                  <NavigationOverview node={visibleNodes[1].curr!} navigationLevel={visibleNodes[1].curr!.level} />
                </Col>
              </Row>
              <NavigationSeriesView className={"scha__nav__series-table"} />
            </>
          )}
        </div>
      </SimpleMenu>
    </>
  );
};

const FADE_IN = "scha__fade-in-out__element scha__fade-in-out__fade-in";
const FADE_OUT = "scha__fade-in-out__element scha__fade-in-out__fade-out--abs-position";

interface NavigationColumnProps {
  parentNode: NavigationNode;
  active: boolean;
}

const NavigationColumn = ({ parentNode, active }: NavigationColumnProps) => {
  let fadeCls;
  if (active) {
    fadeCls = FADE_IN;
  } else {
    fadeCls = FADE_OUT;
  }

  const nodeLevel = parentNode.level;

  return (
    <div className={fadeCls}>
      {nodeLevel !== -1 && <NavigationOverview node={parentNode} navigationLevel={nodeLevel} />}
      <MobileNavigationList parentNode={parentNode} />
    </div>
  );
};

const getHighestSelectedLevel = (visibleNodes: { curr?: unknown; prev?: unknown }[]) => {
  for (let i = 0; ; i++) {
    if (!visibleNodes[i] || !visibleNodes[i].curr) {
      return i;
    }
  }
};
